import React, { useState, useEffect, useRef } from 'react';
import { Activity, Zap, ChevronDown } from 'lucide-react';
import { useSyncOptionChainStore } from '../../../store/syncOptionChainStore';
import server from '../../../utils/serverConfig';
import apiClient from '../../../utils/apiClient';
import { encryptApiKey } from '../../../utils/cryptoUtils';

const STRIKE_STEPS = {
    GOLD: { MCX: 1000, COMEX: 10 },
    SILVER: { MCX: 1000, COMEX: 0.5 },
    CRUDEOIL: { MCX: 100, COMEX: 1 },
    NATURALGAS: { MCX: 5, COMEX: 0.2 }
};

const CONVERSION_RATIOS = {
    GOLD: { comex: 1, mcx: 3 },
    SILVER: { comex: 1, mcx: 5 },
    NATURALGAS: { comex: 1, mcx: 8 },
    CRUDEOIL: { comex: 1, mcx: 10 },
};

const LOT_SIZES = {
    GOLD: { comex: 100, mcx: 100 },
    SILVER: { comex: 5000, mcx: 30 },
    CRUDEOIL: { comex: 1000, mcx: 100 },
    NATURALGAS: { comex: 10000, mcx: 1250 },
};

const SOCKET_URL = "wss://godseye.ws.server.sypto.xyz/ws/global_ltp";

const SyncArbitrageView = ({ activeCommodityTab, setActiveCommodityTab }) => {
    const chainData = useSyncOptionChainStore(state => state.chainData);
    const futuresData = useSyncOptionChainStore(state => state.futuresData);
    const updateOptionChain = useSyncOptionChainStore(state => state.updateOptionChain);
    const deleteOption = useSyncOptionChainStore(state => state.deleteOption);
    const updateFutures = useSyncOptionChainStore(state => state.updateFutures);
    const setStatus = useSyncOptionChainStore(state => state.setStatus);

    const ws = useRef(null);
    const isMounted = useRef(true);

    const [expiries, setExpiries] = useState({
        MCX: { GOLD: [], SILVER: [], CRUDEOIL: [], NATURALGAS: [] },
        COMEX: { GOLD: [], SILVER: [], CRUDEOIL: [], NATURALGAS: [] },
        MCX_FUTURES: { GOLD: [], SILVER: [], CRUDEOIL: [], NATURALGAS: [] },
        COMEX_FUTURES: { GOLD: [], SILVER: [], CRUDEOIL: [], NATURALGAS: [] }
    });
    const [usdinrRate, setUsdinrRate] = useState(null);
    const [selectedExpiries, setSelectedExpiries] = useState({
        MCX: { GOLD: null, SILVER: null, CRUDEOIL: null, NATURALGAS: null },
        COMEX: { GOLD: null, SILVER: null, CRUDEOIL: null, NATURALGAS: null }
    });
    const [futuresDiffMode, setFuturesDiffMode] = useState('pct');
    const [stableComexAtm, setStableComexAtm] = useState({}); // {GOLD: 2750, ...}
    const [stableUsdinr, setStableUsdinr] = useState(null);
    const [lastTriggerPrices, setLastTriggerPrices] = useState({}); // {GOLD: 2750.4, ...}

    useEffect(() => {
        const fetchExpiries = async () => {
            try {
                const commodities = ['GOLD', 'SILVER', 'CRUDEOIL', 'NATURALGAS'];
                const newExpiries = { MCX: {}, COMEX: {}, MCX_FUTURES: {}, COMEX_FUTURES: {} };
                const newSelected = { MCX: {}, COMEX: {} };

                const filterFutureExpiries = (list) => {
                    const todayIST = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
                    todayIST.setHours(0, 0, 0, 0);
                    return (list || []).filter(dateStr => {
                        const d = new Date(dateStr);
                        return !isNaN(d) && d >= todayIST;
                    });
                };

                for (const commodity of commodities) {
                    const res = await apiClient.get(`${server.godseye.baseUrl}api/expiries?underlying=${commodity}`);
                    const data = res.data;

                    newExpiries.MCX[commodity] = filterFutureExpiries(data.MCX);
                    newExpiries.COMEX[commodity] = filterFutureExpiries(data.COMEX);
                    newExpiries.MCX_FUTURES[commodity] = filterFutureExpiries(data.MCX_FUTURES || data.MCX);
                    newExpiries.COMEX_FUTURES[commodity] = filterFutureExpiries(data.COMEX_FUTURES || data.COMEX);

                    if (data.MCX && data.MCX.length > 0) newSelected.MCX[commodity] = data.MCX[0];
                    if (data.COMEX && data.COMEX.length > 0) newSelected.COMEX[commodity] = data.COMEX[0];
                }

                setExpiries(newExpiries);
                setSelectedExpiries(newSelected);
            } catch (error) {
                console.error("Failed to fetch expiries:", error);
            }
        };

        fetchExpiries();
        connectWebSocket();
        return () => {
            isMounted.current = false;
            if (ws.current) {
                ws.current.close();
            }
        };
    }, []);

    const connectWebSocket = () => {
        const apiKey = process.env.REACT_APP_API_KEY || "godseye_frontend_key_123";
        const secretKey = process.env.REACT_APP_SECRET_KEY || "godseye_secret_key_456";
        const token = encryptApiKey(apiKey, secretKey);
        
        ws.current = new WebSocket(`${SOCKET_URL}?token=${token}`);
        ws.current.onopen = () => setStatus("connected");

        ws.current.onmessage = (event) => {
            try {
                const rawMessage = JSON.parse(event.data);

                const processMessage = (message) => {
                    // Store ignores DELETE messages now, but we can skip early here too
                    if (message.op === "DELETE") return;

                    if (message.type === "FUTURE") {
                        updateFutures(message);
                    } else if (message.type === "USDINR") {
                        if (message.ltp) setUsdinrRate(parseFloat(message.ltp));
                    }
                    else if (message.strike && (message.type === "CE" || message.type === "PE")) {
                        // Allow is_sync MCX ticks OR normal Comex/Nymex ticks
                        if (message.is_sync || message.exchange === "COMEX" || message.exchange === "NYMEX") {
                            updateOptionChain(message);
                        }
                    }
                };

                if (Array.isArray(rawMessage)) {
                    rawMessage.forEach(processMessage);
                } else {
                    processMessage(rawMessage);
                }
            } catch (err) {
                console.error("Error parsing WS message:", err);
            }
        };

        ws.current.onclose = () => {
            if (isMounted.current) {
                setStatus("disconnected");
                setTimeout(connectWebSocket, 3000);
            }
        };
    };

    const getPercent = (value, total) => {
        if (!total || total === 0) return "0.00";
        return ((value / total) * 100).toFixed(2);
    };

    const calculateStraddle = (ceLtp, peLtp, strike) => {
        if (!ceLtp || !peLtp || !strike) return "-";
        return getPercent(ceLtp + peLtp, strike);
    };

    const getAtmStrike = (ltp, step) => {
        if (!ltp) return null;
        return Math.round(ltp / step) * step;
    };

    const formatLtt = (timestamp) => {
        if (!timestamp) return "-";
        try {
            return new Date(timestamp * 1000).toLocaleString("en-GB", {
                timeZone: "Asia/Kolkata",
                day: '2-digit', month: 'short', hour12: false, hour: '2-digit', minute: '2-digit'
            });
        } catch (e) { return "-"; }
    };

    const selectedMcxExpiry = selectedExpiries.MCX[activeCommodityTab];
    const selectedComexExpiry = selectedExpiries.COMEX[activeCommodityTab];

    const exchangeKey = (activeCommodityTab === 'CRUDEOIL' || activeCommodityTab === 'NATURALGAS') ? 'NYMEX' : 'COMEX';
    const currentOkFuture = futuresData[`${exchangeKey}_${activeCommodityTab}_${selectedComexExpiry}`] || {};
    const currentMcxFuture = futuresData[`MCX_${activeCommodityTab}_${selectedMcxExpiry}`] || {};

    const mcxLtp = currentMcxFuture?.ltp;
    const comexLtp = currentOkFuture?.ltp;

    // Hysteresis Logic for Frontend ATM anchor
    useEffect(() => {
        if (!comexLtp) return;
        const commodity = activeCommodityTab;
        const step = STRIKE_STEPS[commodity]?.COMEX || 10;
        const currentStable = stableComexAtm[commodity];
        const lastTrigger = lastTriggerPrices[commodity];

        const isInitial = !currentStable || !lastTrigger || !stableUsdinr;
        const isSignificantMove = lastTrigger && Math.abs(comexLtp - lastTrigger) >= Math.max(2.0, step * 0.5);

        if (isInitial || isSignificantMove) {
            const newAtm = Math.round(comexLtp / step) * step;
            setStableComexAtm(prev => ({ ...prev, [commodity]: newAtm }));
            setLastTriggerPrices(prev => ({ ...prev, [commodity]: comexLtp }));
            if (usdinrRate) setStableUsdinr(usdinrRate);
        }
    }, [comexLtp, activeCommodityTab, usdinrRate, stableComexAtm, lastTriggerPrices, stableUsdinr]);

    const comexAtm = stableComexAtm[activeCommodityTab];
    const mcxAtm = getAtmStrike(mcxLtp, STRIKE_STEPS[activeCommodityTab]?.MCX || 1000);

    // We search only in the selected MCX expiry key
    const currentMcxChain = chainData[activeCommodityTab]?.[selectedMcxExpiry] || {};
    const currentComexChain = chainData[activeCommodityTab]?.[selectedComexExpiry] || {};

    // NO MORE findSyncMcxData loop — we use currentMcxChain[strike].MCX

    const getExpiryLtp = (exchange, commodity, index) => {
        const exKey = exchange === 'COMEX' && (commodity === 'CRUDEOIL' || commodity === 'NATURALGAS') ? 'NYMEX' : exchange;
        const expiryList = expiries[`${exchange}_FUTURES`][commodity] || [];
        const expiry = expiryList[index];
        if (!expiry) return null;
        return futuresData[`${exKey}_${commodity}_${expiry}`]?.ltp ?? null;
    };

    const renderExpiryCell = (exchange, commodity, index, isFutureTable = false, compareToIdx = null, align = "end") => {
        const exKey = exchange === 'COMEX' && (commodity === 'CRUDEOIL' || commodity === 'NATURALGAS') ? 'NYMEX' : exchange;
        const expiryList = isFutureTable ? expiries[`${exchange}_FUTURES`][commodity] || [] : expiries[exchange][commodity] || [];
        const expiry = expiryList[index];
        if (!expiry) return <span className="text-slate-600 font-mono">-</span>;

        const futureKey = `${exKey}_${commodity}_${expiry}`;
        const ltp = futuresData[futureKey]?.ltp;

        let diffBadge = null;
        if (compareToIdx !== null && ltp) {
            const baseLtp = getExpiryLtp(exchange, commodity, compareToIdx);
            if (baseLtp) {
                const diffValues = futuresDiffMode === 'pct'
                    ? { val: ((ltp - baseLtp) / baseLtp) * 100, format: (v) => `${v.toFixed(3)}%` }
                    : { val: ltp - baseLtp, format: (v) => (exchange === 'MCX' ? `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`) };
                const isPos = diffValues.val >= 0;
                diffBadge = (
                    <span className={`inline-flex items-center gap-0.5 font-mono text-[9px] font-bold px-1 py-px rounded mt-0.5 ${isPos ? 'text-emerald-400 bg-emerald-500/10' : 'text-red-400 bg-red-500/10'}`}>
                        {isPos ? '▲' : '▼'} {diffValues.format(Math.abs(diffValues.val))}
                    </span>
                );
            }
        }

        return (
            <div className={`flex flex-col items-${align} justify-center w-full`}>
                <span className="text-[9px] text-slate-500 font-medium mb-0.5 leading-none">{expiry}</span>
                <div className="flex items-center gap-1.5">
                    <span className={`font-mono text-xs leading-tight ${ltp ? "text-emerald-400 font-bold" : "text-slate-500"}`}>
                        {ltp ? (exchange === 'MCX' ? `₹${ltp.toLocaleString('en-IN')}` : `$${ltp.toLocaleString()}`) : '-'}
                    </span>
                    {diffBadge}
                </div>
            </div>
        );
    };

    return (
        <>
            {/* Sync Arbitrage label */}
            <div className="mb-3 flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 rounded-full px-3 py-1">
                    ⚡ Sync Mode — MCX strikes derived from Comex price × USDINR × ratio
                </span>
                {usdinrRate && (
                    <span className="text-[10px] font-mono text-blue-400 bg-blue-500/10 border border-blue-500/20 rounded-full px-3 py-1">
                        USDINR ₹{usdinrRate.toFixed(4)}
                    </span>
                )}
            </div>

            {/* Unified Arbitrage Table — identical layout to ArbitrageView */}
            <div className="flex flex-col flex-shrink-0 mb-6 bg-[#1e293b] rounded-xl border border-slate-700/50 shadow-xl overflow-hidden backdrop-blur-sm">
                <div className="flex-1 overflow-auto custom-scrollbar">
                    <table className="w-full text-sm border-collapse">
                        <thead className="sticky top-0 bg-[#0f172a] z-20 shadow-sm border-b border-slate-700">
                            <tr>
                                {/* MCX HEADER */}
                                <th colSpan={8} className="py-3 px-4 text-left border-r border-slate-700/50 bg-[#1e293b]/50">
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-3">
                                            <div className="p-1.5 rounded-lg bg-indigo-500/10 text-indigo-400"><Activity size={16} /></div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">MCX {activeCommodityTab}</span>
                                                    <div className="relative group">
                                                        <select
                                                            value={selectedMcxExpiry || ""}
                                                            onChange={(e) => setSelectedExpiries(prev => ({ ...prev, MCX: { ...prev.MCX, [activeCommodityTab]: e.target.value } }))}
                                                            className="appearance-none bg-slate-800/80 text-[10px] font-semibold text-emerald-400 font-mono rounded-md pl-2 pr-6 py-1 outline-none border border-slate-700 hover:border-indigo-500/50 focus:border-indigo-500 cursor-pointer"
                                                        >
                                                            {expiries.MCX[activeCommodityTab]?.map(opt => <option key={opt} value={opt} className="bg-slate-900 text-slate-300">{opt}</option>)}
                                                        </select>
                                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-1 text-slate-500"><ChevronDown size={12} /></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-base font-mono font-bold text-slate-200">₹{mcxLtp ? mcxLtp.toLocaleString() : '-'}</div>
                                        </div>
                                    </div>
                                </th>

                                {/* MIDDLE HEADER */}
                                <th colSpan={7} className="py-2 px-4 text-center border-r border-slate-700/50 bg-[#0f172a] text-slate-400 font-bold tracking-widest text-[10px] uppercase">
                                    <div className="flex items-center justify-center gap-2">
                                        <span>Arbitrage Diffs</span>
                                        {usdinrRate && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 font-mono">USDINR: ₹{usdinrRate.toFixed(2)}</span>}
                                    </div>
                                </th>

                                {/* COMEX HEADER */}
                                <th colSpan={8} className="py-3 px-4 text-left bg-[#1e293b]/50">
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-3">
                                            <div className="p-1.5 rounded-lg bg-yellow-500/10 text-yellow-500"><Zap size={16} /></div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">{exchangeKey} {activeCommodityTab}</span>
                                                    <div className="relative group">
                                                        <select
                                                            value={selectedComexExpiry || ""}
                                                            onChange={(e) => setSelectedExpiries(prev => ({ ...prev, COMEX: { ...prev.COMEX, [activeCommodityTab]: e.target.value } }))}
                                                            className="appearance-none bg-slate-800/80 text-[10px] font-semibold text-yellow-500 font-mono rounded-md pl-2 pr-6 py-1 outline-none border border-slate-700 hover:border-yellow-500/50 focus:border-yellow-500 cursor-pointer"
                                                        >
                                                            {expiries.COMEX[activeCommodityTab]?.map(opt => <option key={opt} value={opt} className="bg-slate-900 text-slate-300">{opt}</option>)}
                                                        </select>
                                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-1 text-slate-500"><ChevronDown size={12} /></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-base font-mono font-bold text-yellow-400">${comexLtp ? comexLtp.toLocaleString() : '-'}</div>
                                        </div>
                                    </div>
                                </th>
                            </tr>

                            <tr className="text-[10px] uppercase text-slate-500 font-semibold bg-[#1e293b]">
                                <th className="py-3 px-2 text-right text-orange-400">Straddle</th>
                                <th className="py-3 px-1 text-center">Time</th>
                                <th className="py-3 px-2 text-right font-bold text-slate-300">CE LTP</th>
                                <th className="py-3 px-2 pr-8 text-right">CE %</th>
                                <th className="py-3 px-2 text-left font-bold text-slate-300">PE LTP</th>
                                <th className="py-3 px-2 text-left">PE %</th>
                                <th className="py-3 px-1 text-center">Time</th>
                                <th className="py-3 px-4 text-center bg-[#0f172a] text-indigo-400 border-x border-slate-700/50 w-24">Strike</th>

                                <th className="py-3 px-2 text-center text-green-400 border-l border-slate-700/50">C CE Δ₹</th>
                                <th className="py-3 px-2 text-center text-green-400">Net CE Δ₹</th>
                                <th className="py-3 px-2 text-center text-green-400">M CE Δ₹</th>
                                <th className="py-3 px-2 text-center text-red-400">C PE Δ₹</th>
                                <th className="py-3 px-2 text-center text-red-400">Net PE Δ₹</th>
                                <th className="py-3 px-2 text-center text-red-400">M PE Δ₹</th>
                                <th className="py-3 px-2 text-center text-orange-400 border-r border-slate-700/50">Std Δ₹</th>

                                <th className="py-3 px-4 text-center bg-[#0f172a] text-yellow-500 border-x border-slate-700/50 w-24">Strike</th>
                                <th className="py-3 px-1 text-center">Time</th>
                                <th className="py-3 px-2 text-right font-bold text-slate-300">CE LTP</th>
                                <th className="py-3 px-2 pr-8 text-right">CE %</th>
                                <th className="py-3 px-2 text-left font-bold text-slate-300">PE LTP</th>
                                <th className="py-3 px-2 text-left">PE %</th>
                                <th className="py-3 px-1 text-center">Time</th>
                                <th className="py-3 px-2 text-left text-orange-400">Straddle</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/30">
                            {(() => {
                                if (!comexAtm) return (
                                    <tr><td colSpan={19} className="text-center py-8 text-slate-500">
                                        {!comexLtp ? 'Waiting for Comex futures price...' : 'Waiting for market data...'}
                                    </td></tr>
                                );

                                const mcxStep = STRIKE_STEPS[activeCommodityTab]?.MCX || 1000;
                                const comexStep = STRIKE_STEPS[activeCommodityTab]?.COMEX || 10;

                                // Derive MCX ATM from Comex price using stable anchor
                                const ratio = CONVERSION_RATIOS[activeCommodityTab] || { comex: 1, mcx: 1 };
                                const syncMcxAtm = stableUsdinr
                                    ? Math.round((comexAtm * stableUsdinr) / mcxStep) * mcxStep
                                    : mcxAtm;

                                const rows = [];
                                for (let i = -3; i <= 3; i++) {
                                    // Comex strike: standard ATM ± step
                                    const comexStrikeRaw = comexAtm + (i * comexStep);
                                    let comexStrike = comexStrikeRaw;
                                    let comexStrikeKey = comexStrikeRaw.toString();

                                    if (comexStep < 1) {
                                        // e.g. 24.5 or 24.0 (but chain data usually stores it as '24.5' or '24')
                                        comexStrike = parseFloat(comexStrikeRaw.toFixed(2));
                                        comexStrikeKey = comexStrike.toString();
                                    }

                                    // MCX strike: derived from this Comex strike using STABLE anchor
                                    const mcxStrike = stableUsdinr
                                        ? Math.round((comexStrike * stableUsdinr) / mcxStep) * mcxStep
                                        : (syncMcxAtm + i * mcxStep);

                                    // Strict Expiry lookup
                                    const mcxData = currentMcxChain[mcxStrike]?.MCX || {};
                                    const comexData = currentComexChain[comexStrikeKey]?.[exchangeKey] || currentComexChain[comexStrike]?.[exchangeKey] || {};

                                    const mcxStraddle = calculateStraddle(mcxData.ce?.ltp, mcxData.pe?.ltp, mcxStrike);
                                    const comexStraddle = calculateStraddle(comexData.ce?.ltp, comexData.pe?.ltp, comexStrike);

                                    const isMidCe = comexData.ce?.is_mid;
                                    const isMidPe = comexData.pe?.is_mid;

                                    const isValid = (val) => val !== undefined && val !== null && val > 0;
                                    const mcxCeLtp = mcxData.ce?.ltp;
                                    const comexCeLtp = comexData.ce?.ltp;
                                    const mcxPeLtp = mcxData.pe?.ltp;
                                    const comexPeLtp = comexData.pe?.ltp;

                                    const displayUsdinr = stableUsdinr || usdinrRate;

                                    // INR conversion using displayUsdinr
                                    const comexCeInr = (isValid(comexCeLtp) && displayUsdinr) ? comexCeLtp * ratio.comex * displayUsdinr : null;
                                    const mcxCeInr = isValid(mcxCeLtp) ? mcxCeLtp * ratio.mcx : null;
                                    const comexPeInr = (isValid(comexPeLtp) && displayUsdinr) ? comexPeLtp * ratio.comex * displayUsdinr : null;
                                    const mcxPeInr = isValid(mcxPeLtp) ? mcxPeLtp * ratio.mcx : null;

                                    const lots = LOT_SIZES[activeCommodityTab] || { comex: 1, mcx: 1 };
                                    const comexCeDeltaInr = (isValid(comexCeLtp) && displayUsdinr) ? comexCeLtp * lots.comex * ratio.comex * displayUsdinr : null;
                                    const mcxCeDeltaInr = isValid(mcxCeLtp) ? mcxCeLtp * lots.mcx * ratio.mcx : null;
                                    const comexPeDeltaInr = (isValid(comexPeLtp) && displayUsdinr) ? comexPeLtp * lots.comex * ratio.comex * displayUsdinr : null;
                                    const mcxPeDeltaInr = isValid(mcxPeLtp) ? mcxPeLtp * lots.mcx * ratio.mcx : null;

                                    const pctDiff = (a, b) => {
                                        if (!a || !b) return null;
                                        const avg = (a + b) / 2;
                                        return avg === 0 ? null : (Math.abs(a - b) / avg * 100);
                                    };

                                    const diffCe = pctDiff(comexCeInr, mcxCeInr);
                                    const diffPe = pctDiff(comexPeInr, mcxPeInr);

                                    const deltaCe = (comexCeDeltaInr && mcxCeDeltaInr) ? (comexCeDeltaInr - mcxCeDeltaInr).toFixed(2) : null;
                                    const deltaPe = (comexPeDeltaInr && mcxPeDeltaInr) ? (comexPeDeltaInr - mcxPeDeltaInr).toFixed(2) : null;

                                    const comexStraddleInr = (comexCeInr && comexPeInr) ? comexCeInr + comexPeInr : null;
                                    const mcxStraddleInr = (mcxCeInr && mcxPeInr) ? mcxCeInr + mcxPeInr : null;
                                    const diffStraddlePct = pctDiff(comexStraddleInr, mcxStraddleInr);

                                    const comexStraddleDeltaInr = (comexCeDeltaInr && comexPeDeltaInr) ? comexCeDeltaInr + comexPeDeltaInr : null;
                                    const mcxStraddleDeltaInr = (mcxCeDeltaInr && mcxPeDeltaInr) ? mcxCeDeltaInr + mcxPeDeltaInr : null;
                                    const deltaStraddle = (comexStraddleDeltaInr && mcxStraddleDeltaInr) ? (comexStraddleDeltaInr - mcxStraddleDeltaInr).toFixed(2) : null;

                                    const mcxCeChg = mcxData.ce?.ltp_change;
                                    const comexCeChg = comexData.ce?.ltp_change;
                                    const mcxPeChg = mcxData.pe?.ltp_change;
                                    const comexPeChg = comexData.pe?.ltp_change;

                                    const isAtm = i === 0;

                                    rows.push(
                                        <tr key={`${activeCommodityTab}_${comexStrike}`} className={`hover:bg-slate-700/20 transition-colors group ${isAtm ? 'bg-indigo-500/5' : ''}`}>
                                            {/* MCX cols */}
                                            <td className="py-3 px-2 text-right font-mono text-xs text-orange-400 font-bold">{mcxStraddle}%</td>
                                            <td className="py-3 px-1 text-center font-mono text-[10px] text-slate-300">{formatLtt(mcxData.ce?.ltt) || '-'}</td>
                                            <td className="py-3 px-2 text-right font-mono text-sm font-medium text-emerald-400">{mcxData.ce?.ltp || '-'}</td>
                                            <td className={`py-3 px-2 pr-8 text-right font-mono text-xs ${mcxCeChg >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                                                {mcxData.ce?.ltp_change ? `${parseFloat(mcxData.ce.ltp_change).toFixed(2)}%` : '-'}
                                            </td>
                                            <td className="py-3 px-2 text-left font-mono text-sm font-medium text-rose-400">{mcxData.pe?.ltp || '-'}</td>
                                            <td className={`py-3 px-2 text-left font-mono text-xs ${mcxPeChg >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                                                {mcxData.pe?.ltp_change ? `${parseFloat(mcxData.pe.ltp_change).toFixed(2)}%` : '-'}
                                            </td>
                                            <td className="py-3 px-1 text-center font-mono text-[10px] text-slate-300">{formatLtt(mcxData.pe?.ltt) || '-'}</td>
                                            <td className="py-3 px-4 text-center font-mono font-bold text-slate-300 bg-slate-800/30 border-x border-slate-700/50 group-hover:bg-indigo-500/20 transition-colors">
                                                {mcxStrike}
                                                {isAtm && <span className="ml-1 text-[9px] text-indigo-400">ATM</span>}
                                            </td>

                                            {/* Diff cols */}
                                            <td className={`py-3 px-2 text-center font-mono text-xs border-l border-slate-700/50 ${comexCeDeltaInr ? (comexCeDeltaInr >= 0 ? 'text-emerald-400' : 'text-rose-400') : 'text-slate-500'}`}>
                                                {comexCeDeltaInr ? `₹${parseFloat(comexCeDeltaInr).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-'}
                                            </td>
                                            <td className={`py-3 px-2 text-center font-mono text-xs ${deltaCe ? (parseFloat(deltaCe) >= 0 ? 'text-emerald-400' : 'text-rose-400') : 'text-slate-500'}`}>
                                                {deltaCe ? `₹${parseFloat(deltaCe).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-'}
                                            </td>
                                            <td className={`py-3 px-2 text-center font-mono text-xs ${mcxCeDeltaInr ? (mcxCeDeltaInr >= 0 ? 'text-emerald-400' : 'text-rose-400') : 'text-slate-500'}`}>
                                                {mcxCeDeltaInr ? `₹${parseFloat(mcxCeDeltaInr).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-'}
                                            </td>

                                            <td className={`py-3 px-2 text-center font-mono text-xs border-l border-slate-700/50 ${comexPeDeltaInr ? (comexPeDeltaInr >= 0 ? 'text-emerald-400' : 'text-rose-400') : 'text-slate-500'}`}>
                                                {comexPeDeltaInr ? `₹${parseFloat(comexPeDeltaInr).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-'}
                                            </td>
                                            <td className={`py-3 px-2 text-center font-mono text-xs ${deltaPe ? (parseFloat(deltaPe) >= 0 ? 'text-emerald-400' : 'text-rose-400') : 'text-slate-500'}`}>
                                                {deltaPe ? `₹${parseFloat(deltaPe).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-'}
                                            </td>
                                            <td className={`py-3 px-2 text-center font-mono text-xs ${mcxPeDeltaInr ? (mcxPeDeltaInr >= 0 ? 'text-emerald-400' : 'text-rose-400') : 'text-slate-500'}`}>
                                                {mcxPeDeltaInr ? `₹${parseFloat(mcxPeDeltaInr).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-'}
                                            </td>

                                            <td className={`py-3 px-2 text-center font-mono text-xs border-r border-slate-700/50 ${deltaStraddle ? (parseFloat(deltaStraddle) >= 0 ? 'text-emerald-400' : 'text-rose-400') : 'text-slate-500'}`}>
                                                {deltaStraddle ? `₹${parseFloat(deltaStraddle).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-'}
                                            </td>

                                            {/* Comex cols */}
                                            <td className="py-3 px-4 text-center font-mono font-bold text-slate-300 bg-slate-800/30 border-x border-slate-700/50 group-hover:bg-yellow-500/20 transition-colors">
                                                {comexStrike}
                                                {isAtm && <span className="ml-1 text-[9px] text-yellow-400">ATM</span>}
                                            </td>
                                            <td className="py-3 px-1 text-center font-mono text-[10px] text-slate-300">{formatLtt(comexData.ce?.ltt) || '-'}</td>
                                            <td className="py-3 px-2 text-right font-mono text-sm font-medium text-emerald-400">
                                                <div className="flex items-center justify-end gap-1">
                                                    {comexData.ce?.ltp || '-'}
                                                    {isMidCe && <span className="text-[9px] px-1 rounded bg-yellow-500/20 text-yellow-500 font-bold">Mid</span>}
                                                </div>
                                            </td>
                                            <td className={`py-3 px-2 pr-8 text-right font-mono text-xs ${comexCeChg >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                                                {comexData.ce?.ltp_change ? `${parseFloat(comexData.ce.ltp_change).toFixed(2)}%` : '-'}
                                            </td>
                                            <td className="py-3 px-2 text-left font-mono text-sm font-medium text-rose-400">
                                                <div className="flex items-center gap-1">
                                                    {isMidPe && <span className="text-[9px] px-1 rounded bg-yellow-500/20 text-yellow-500 font-bold">Mid</span>}
                                                    {comexData.pe?.ltp || '-'}
                                                </div>
                                            </td>
                                            <td className={`py-3 px-2 text-left font-mono text-xs ${comexPeChg >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                                                {comexData.pe?.ltp_change ? `${parseFloat(comexData.pe.ltp_change).toFixed(2)}%` : '-'}
                                            </td>
                                            <td className="py-3 px-1 text-center font-mono text-[10px] text-slate-300">{formatLtt(comexData.pe?.ltt) || '-'}</td>
                                            <td className="py-3 px-2 text-left font-mono text-xs text-orange-400 font-bold">{comexStraddle}%</td>
                                        </tr>
                                    );
                                }
                                return rows;
                            })()}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Futures Term Structure Table */}
            <div className="flex flex-col shrink-0 mb-6 bg-[#1e293b] rounded-xl border border-slate-700/50 shadow-xl overflow-hidden backdrop-blur-sm">
                <div className="px-3 py-2 bg-[#0f172a] border-b border-slate-700/50 flex items-center justify-between">
                    <h3 className="text-xs font-bold text-slate-200 flex items-center gap-2">
                        <Activity size={14} className="text-indigo-400" />
                        Futures Term Structure
                    </h3>
                    <div className="flex items-center bg-slate-800/80 p-0.5 rounded-lg border border-slate-700/80">
                        <button onClick={() => setFuturesDiffMode('pct')} className={`px-3 py-1 rounded-md text-[10px] font-bold tracking-wider transition-all duration-200 ${futuresDiffMode === 'pct' ? 'bg-indigo-500 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'}`}>% DIFF</button>
                        <button onClick={() => setFuturesDiffMode('inr')} className={`px-3 py-1 rounded-md text-[10px] font-bold tracking-wider transition-all duration-200 ${futuresDiffMode === 'inr' ? 'bg-indigo-500 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'}`}>PRICE DIFF</button>
                    </div>
                </div>
                <div className="overflow-x-auto custom-scrollbar">
                    <table className="w-full text-xs border-collapse">
                        <thead className="bg-[#1e293b]/80 border-b border-slate-700/50">
                            <tr>
                                <th className="py-1.5 px-3 text-left text-[9px] uppercase font-bold text-slate-400 tracking-wider w-32 border-r border-slate-700/30">Asset</th>
                                <th className="py-1.5 px-3 text-center text-[9px] uppercase font-bold text-slate-400 tracking-wider w-24 border-r border-slate-700/30">Exchange</th>
                                <th className="py-1.5 px-3 text-center text-[9px] uppercase font-bold text-slate-400 tracking-wider border-r border-slate-700/30">Current Expiry</th>
                                <th className="py-1.5 px-3 text-center text-[9px] uppercase font-bold text-slate-400 tracking-wider border-r border-slate-700/30">Next Expiry</th>
                                <th className="py-1.5 px-3 text-center text-[9px] uppercase font-bold text-slate-400 tracking-wider">Next+1 Expiry</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/30 bg-[#0f172a]/20">
                            {['GOLD', 'SILVER', 'CRUDEOIL', 'NATURALGAS'].map((commodity, idx) => (
                                <React.Fragment key={commodity}>
                                    <tr className={`hover:bg-slate-700/10 transition-colors ${idx % 2 === 0 ? 'bg-transparent' : 'bg-[#1e293b]/10'}`}>
                                        <td className="py-1.5 px-3 text-[11px] font-bold text-slate-200 border-r border-slate-700/30 align-middle" rowSpan={2}>
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center justify-center w-5 h-5 rounded bg-slate-800/80 border border-slate-600/50 shadow-inner">
                                                    <div className={`w-2 h-2 rounded-full ${commodity === 'GOLD' ? 'bg-yellow-500' : commodity === 'SILVER' ? 'bg-slate-300' : commodity === 'CRUDEOIL' ? 'bg-black border border-slate-500' : 'bg-blue-400'}`}></div>
                                                </div>
                                                <span className="tracking-wide">{commodity}</span>
                                            </div>
                                        </td>
                                        <td className="py-1.5 px-3 text-center align-middle border-r border-slate-700/30">
                                            <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-indigo-500/10 border border-indigo-500/20 text-[9px] font-bold text-indigo-400 uppercase tracking-widest min-w-[50px]">MCX</span>
                                        </td>
                                        <td className="py-1.5 px-3 bg-indigo-500/5 border-r border-slate-700/30">{renderExpiryCell('MCX', commodity, 0, true, null, "center")}</td>
                                        <td className="py-1.5 px-3 bg-indigo-500/5 border-r border-slate-700/30">{renderExpiryCell('MCX', commodity, 1, true, 0, "center")}</td>
                                        <td className="py-1.5 px-3 bg-indigo-500/5">{renderExpiryCell('MCX', commodity, 2, true, 0, "center")}</td>
                                    </tr>
                                    <tr className={`hover:bg-slate-700/10 transition-colors border-b-2 border-slate-700/50 ${idx % 2 === 0 ? 'bg-transparent' : 'bg-[#1e293b]/10'}`}>
                                        <td className="py-1.5 px-3 text-center align-middle border-r border-slate-700/30">
                                            <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-yellow-500/10 border border-yellow-500/20 text-[9px] font-bold text-yellow-500 uppercase tracking-widest min-w-[50px]">
                                                {(commodity === 'CRUDEOIL' || commodity === 'NATURALGAS') ? 'NYMEX' : 'COMEX'}
                                            </span>
                                        </td>
                                        <td className="py-1.5 px-3 bg-yellow-500/5 border-r border-slate-700/30">{renderExpiryCell('COMEX', commodity, 0, true, null, "center")}</td>
                                        <td className="py-1.5 px-3 bg-yellow-500/5 border-r border-slate-700/30">{renderExpiryCell('COMEX', commodity, 1, true, 0, "center")}</td>
                                        <td className="py-1.5 px-3 bg-yellow-500/5">{renderExpiryCell('COMEX', commodity, 2, true, 0, "center")}</td>
                                    </tr>
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};

export default SyncArbitrageView;
